import { createContext } from "react";

import { type IAnalyticsContextData } from "./payload";

/**
 * The value carried by {@link AnalyticsContext}.
 *
 * Deliberately a *getter* rather than the data itself. If the context value were
 * the merged data, every `pageName` change would give the provider a new value —
 * and, because the provider component is produced per-render, a new component
 * *type* — so React would unmount and remount the entire subtree on each route
 * change rather than re-rendering it. At the ~2,400 subscribers that
 * instrumenting BTButton would add, that resets local state, focus, and any
 * in-progress interaction beneath the provider.
 *
 * Holding a stable getter that reads a ref is how `react-tracking` (the v1
 * pipeline) avoids the same trap: 0 remounts at the same scale, measured in
 * `__perf__/subscriberScale.perf.test.tsx`.
 *
 * The trade is that context reads are pull, not push — `useAnalyticsContext()`
 * returns a snapshot at render time and does not re-render when an ancestor
 * reseeds. That suits this pipeline, whose only consumer reads context at
 * dispatch time inside `trackAnalytics`.
 */
export interface IAnalyticsContextValue {
  getMergedContext: () => IAnalyticsContextData;
}

const EMPTY_CONTEXT: IAnalyticsContextValue = {
  getMergedContext: () => ({}),
};

/**
 * Sawmill v3's own React context for tree-merged event context.
 *
 * Deliberately not `react-tracking`'s. v3 previously reached that context
 * through a `react-tracking-v3` pnpm alias on the assumption that a separate
 * specifier yields a separate module instance — it does not. pnpm resolves the
 * alias to the same physical package as `Clients.App`'s v1 `react-tracking`,
 * and Vite (with `preserveSymlinks` unset) dedupes both to one module, so the
 * two pipelines shared a single `createContext` object. v1's flat keys leaked
 * into v3 events via `context.jobId`, and v3's `pageName` leaked back out
 * through v1's `dispatchTracking` spread into Mixpanel/Qualtrics.
 *
 * Owning ~30 lines of context plumbing makes the isolation structural rather
 * than dependent on module-resolution behavior.
 */
export const AnalyticsContext =
  createContext<IAnalyticsContextValue>(EMPTY_CONTEXT);
